import type { DrawerConfig } from '@/features/drawer/types/DrawerConfig';
import { Suspense, lazy, memo, useMemo } from 'react';
import type { Theme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BaseDrawer } from '@/features/drawer/components/BaseDrawer/BaseDrawer';
import { BaseDrawerLoader } from '@/features/drawer/components/BaseDrawer/components/BaseDrawerLoader/BaseDrawerLoader';
import { drawersConfiguration } from '@/features/drawer/constants/drawersConfiguration';
import { Drawers } from '@/features/drawer/enums/Drawers';
import { useRouterDrawers } from '@/features/drawer/hooks/useRouterDrawers';
import { useMultiAutoFocus } from '@/hooks/accessibility/useMultiAutoFocus';
import { defaultTheme } from '@/styles/theme/defaultTheme/defaultTheme';
import { useDrawerFocusManager } from './hooks/useDrawerFocusManager';

const LazyProducerDrawer = lazy(() => import('@/features/drawer/components/ProducerDrawer/ProducerDrawer'));
const LazyTasksDrawer = lazy(() => import('@/features/drawer/components/TasksDrawer/TasksDrawer'));
const LazyNotesDrawer = lazy(() => import('@/features/drawer/components/NotesDrawer/NotesDrawer'));
const LazyDocumentsDrawer = lazy(() => import('@/features/drawer/components/DocumentsDrawer/DocumentsDrawer'));
const LazyInsuredDrawer = lazy(() => import('@/features/drawer/components/InsuredDrawer/InsuredDrawer'));
const LazyPartiesDrawer = lazy(() => import('@/features/drawer/components/PartiesDrawer/PartiesDrawer'));
const LazyPartyAddressesDrawer = lazy(
  () => import('@/features/drawer/components/PartyAddressesDrawer/PartyAddressesDrawer'),
);
const LazyPartyPaymentInformationDrawer = lazy(
  () => import('@/features/drawer/components/PartyPaymentInformation/PartyPaymentInformationDrawer'),
);
const LazyPolicyMatchingDrawer = lazy(
  () => import('@/features/drawer/components/PolicyMatchingDrawer/PolicyMatchingDrawer'),
);
const LazyAdminUserControlDrawer = lazy(
  () => import('@/features/drawer/components/AdminUsersControlDrawer/AdminUsersControlDrawer'),
);
const LazyClaimSetControlDrawer = lazy(
  () => import('@/features/drawer/components/ClaimSetControlDrawer/ClaimSetControlDrawer'),
);
const LazyPartyDetailsControlDrawer = lazy(
  () => import('@/features/drawer/components/PartyControlDrawer/PartyDetailsControlDrawer'),
);
const LazyRecentActivityDrawer = lazy(
  () => import('@/features/drawer/components/RecentActivityDrawer/RecentActivityDrawer'),
);
const LazyContactsDrawer = lazy(() => import('@/features/drawer/components/ContactsDrawer/ContactsDrawer'));
const LazyBeneficiaryDrawer = lazy(() => import('@/features/drawer/components/BeneficiaryDrawer/BeneficiaryDrawer'));
const LazyBenefitCalculationDrawer = lazy(
  () => import('@/features/drawer/components/BenefitCalculationDrawer/BenefitCalculationDrawer'),
);
const LazyCoveragesDrawer = lazy(() => import('@/features/drawer/components/CoveragesDrawer/CoveragesDrawer'));
const LazyMilestonesDrawer = lazy(() => import('@/features/drawer/components/MilestonesDrawer/MilestonesDrawer'));
const LazyPayeeDrawer = lazy(() => import('@/features/drawer/components/PayeeDrawer/PayeeDrawer'));
const LazyTransactionDrawer = lazy(() => import('@/features/drawer/components/TransactionDrawer/TransactionDrawer'));

export const DrawerProvider = memo(() => {
  const activeDrawers = useRouterDrawers();

  const [refs, focusOnFirstInteractiveElement] = useMultiAutoFocus<HTMLDivElement>(activeDrawers, true);

  useDrawerFocusManager(activeDrawers, focusOnFirstInteractiveElement);

  const getContent = (drawerKey: Drawers, configuration: DrawerConfig) => {
    switch (drawerKey) {
      case Drawers.Tasks:
        return <LazyTasksDrawer configuration={configuration} />;
      case Drawers.Beneficiary:
        return <LazyBeneficiaryDrawer configuration={configuration} />;
      case Drawers.BenefitCalculation:
        return <LazyBenefitCalculationDrawer configuration={configuration} />;
      case Drawers.Notes:
        return <LazyNotesDrawer configuration={configuration} />;
      case Drawers.Documents:
        return <LazyDocumentsDrawer configuration={configuration} />;
      case Drawers.Insured:
        return <LazyInsuredDrawer configuration={configuration} />;
      case Drawers.Parties:
        return <LazyPartiesDrawer configuration={configuration} />;
      case Drawers.PolicyMatching:
        return <LazyPolicyMatchingDrawer configuration={configuration} />;
      case Drawers.Contacts:
        return <LazyContactsDrawer configuration={configuration} />;
      case Drawers.ClaimSetControl:
        return <LazyClaimSetControlDrawer configuration={configuration} />;
      case Drawers.PartyControl:
        return <LazyPartyDetailsControlDrawer configuration={configuration} />;
      case Drawers.RecentActivity:
        return <LazyRecentActivityDrawer configuration={configuration} />;
      case Drawers.AdminUsers:
        return <LazyAdminUserControlDrawer configuration={configuration} />;
      case Drawers.Coverages:
        return <LazyCoveragesDrawer configuration={configuration} />;
      case Drawers.Milestones:
        return <LazyMilestonesDrawer configuration={configuration} />;
      case Drawers.Payee:
        return <LazyPayeeDrawer configuration={configuration} />;
      case Drawers.Transaction:
        return <LazyTransactionDrawer configuration={configuration} />;
      case Drawers.PartyAddresses:
        return <LazyPartyAddressesDrawer configuration={configuration} />;
      case Drawers.PartyPaymentInformation:
        return <LazyPartyPaymentInformationDrawer configuration={configuration} />;
      case Drawers.Producer:
        return <LazyProducerDrawer configuration={configuration} />;
      default:
        return null;
    }
  };

  const content = useMemo(() => {
    return activeDrawers.map((drawerKey) => {
      const configuration = drawersConfiguration?.[drawerKey];

      if (!configuration) return null;

      return (
        <ThemeProvider
          theme={(configuration.theme as Theme) || defaultTheme}
          key={`drawer-theme-provider-${drawerKey}`}>
          <BaseDrawer
            key={`drawer-provider-drawer-${drawerKey}`}
            ref={refs[drawerKey]}
            drawerKey={drawerKey}
            configuration={configuration}>
            <Suspense fallback={<BaseDrawerLoader />}>{getContent(drawerKey, configuration)}</Suspense>
          </BaseDrawer>
        </ThemeProvider>
      );
    });
  }, [activeDrawers]);

  return <>{content}</>;
});

DrawerProvider.displayName = 'DrawerProvider';
